import React, { useState, useRef } from 'react';
import { Eye, EyeOff, Upload, Image, Type, Link, Palette, AlignLeft, AlignCenter, AlignRight, MousePointer, Sparkles, Wand2, RotateCcw } from 'lucide-react';

import ImageUploader from '../ImageUploader';


const HeroForm = ({ content = {}, updateContent = () => {}, pageSlug = 'demo' }) => {
  const [activeSection, setActiveSection] = useState('content');
  const [showPreview, setShowPreview] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = (imagePath) => {
    updateContent('imageUrl', imagePath);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      handleImageUpload(url);
    }
  };

  const presetStyles = [
    { name: 'Modern', bg: 'bg-gradient-to-r from-blue-600 to-purple-600', text: 'text-white' },
    { name: 'Elegant', bg: 'bg-gradient-to-r from-gray-900 to-gray-700', text: 'text-white' },
    { name: 'Fresh', bg: 'bg-gradient-to-r from-green-400 to-blue-500', text: 'text-white' },
    { name: 'Warm', bg: 'bg-gradient-to-r from-orange-400 to-pink-400', text: 'text-white' }
  ];

  const buttonStyles = [
    { value: 'primary', name: 'Primary', class: 'bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors' },
    { value: 'secondary', name: 'Secondary', class: 'bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors' },
    { value: 'outline', name: 'Outline', class: 'border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors' },
    { value: 'link', name: 'Link', class: 'text-blue-600 px-6 py-3 rounded-lg font-medium hover:text-blue-800 underline transition-colors' }
  ];

  const alignmentOptions = [
    { value: 'left', icon: AlignLeft, name: 'Left' },
    { value: 'center', icon: AlignCenter, name: 'Center' },
    { value: 'right', icon: AlignRight, name: 'Right' }
  ];

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Wand2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Hero Section Builder</h2>
              <p className="text-indigo-100">Create stunning hero sections with ease</p>
            </div>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showPreview ? 'Hide' : 'Show'} Preview
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Form Section */}
        <div className="flex-1 p-6">
          {/* Navigation Tabs */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
            {[
              { id: 'content', label: 'Content', icon: Type },
              { id: 'design', label: 'Design', icon: Palette },
              { id: 'media', label: 'Media', icon: Image }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                  activeSection === tab.id
                    ? 'bg-white text-indigo-600 shadow-md'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Tab */}
          {activeSection === 'content' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    Hero Title
                  </label>
                  <input
                    type="text"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors text-lg font-medium"
                    placeholder="Enter your compelling headline..."
                    value={content?.title || ''}
                    onChange={(e) => updateContent('title', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">Make it catchy and memorable!</p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <Type className="w-4 h-4 text-indigo-500" />
                    Subtitle
                  </label>
                  <input
                    type="text"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                    placeholder="Add a supporting subtitle..."
                    value={content?.subtitle || ''}
                    onChange={(e) => updateContent('subtitle', e.target.value)}
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <Type className="w-4 h-4 text-indigo-500" />
                    Description
                  </label>
                  <textarea
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors resize-none"
                    rows="4"
                    placeholder="Describe what makes you special..."
                    value={content?.text || ''}
                    onChange={(e) => updateContent('text', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                      <MousePointer className="w-4 h-4 text-indigo-500" />
                      Button Text
                    </label>
                    <input
                      type="text"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                      placeholder="Call to action..."
                      value={content?.buttonText || ''}
                      onChange={(e) => updateContent('buttonText', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                      <Link className="w-4 h-4 text-indigo-500" />
                      Button Link
                    </label>
                    <input
                      type="text"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                      placeholder="https://..."
                      value={content?.buttonLink || ''}
                      onChange={(e) => updateContent('buttonLink', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Design Tab */}
          {activeSection === 'design' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                  <Palette className="w-4 h-4 text-indigo-500" />
                  Button Style
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {buttonStyles.map(style => (
                    <button
                      key={style.value}
                      onClick={() => updateContent('buttonStyle', style.value)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        content?.buttonStyle === style.value
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-left">
                        <p className="font-medium text-sm mb-2">{style.name}</p>
                        <div className={`${style.class} text-xs py-1 px-3 inline-block`}>
                          Preview
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                  <AlignCenter className="w-4 h-4 text-indigo-500" />
                  Text Alignment
                </label>
                <div className="flex bg-gray-100 rounded-xl p-1">
                  {alignmentOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateContent('alignment', option.value)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                        content?.alignment === option.value
                          ? 'bg-white text-indigo-600 shadow-md'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <option.icon className="w-4 h-4" />
                      {option.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Media Tab */}
          {activeSection === 'media' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                  <Image className="w-4 h-4 text-indigo-500" />
                  Hero Image
                </label>
                
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors">
                  {content?.imageUrl ? (
                    <div className={`space-y-4 ${isAnimating ? 'animate-pulse' : ''}`}>
                      <img 
                        src={content.imageUrl} 
                        alt="Hero Preview" 
                        className="max-w-full h-48 object-cover rounded-lg mx-auto shadow-lg"
                      />
                      <div className="flex justify-center gap-2">
                       
                        <ImageUploader 
                          onImageUpload={handleImageUpload} 
                          pageSlug={pageSlug}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                          buttonText="Upload New"
                        />
                        <button
                          onClick={() => updateContent('imageUrl', '')}
                          className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                        <Upload className="w-8 h-8 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-gray-600 mb-4">Upload an image for your hero section</p>
                        <div className="flex justify-center gap-3">
                          
                          <ImageUploader 
                            onImageUpload={handleImageUpload} 
                            pageSlug={pageSlug}
                            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                            buttonText="Upload Image"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Supports JPG, PNG, GIF up to 10MB
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <div className="mt-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Or enter image URL:</label>
                  <input
                    type="text"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                    placeholder="https://example.com/image.jpg"
                    value={content?.imageUrl || ''}
                    onChange={(e) => updateContent('imageUrl', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

       {/* Preview Section */}
       {showPreview && (
          <div className="lg:w-96 bg-gray-50 p-6 border-l">
            <div className="sticky top-6">
              <h3 className="font-bold text-lg mb-4 text-gray-800">Live Preview</h3>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className={`p-8 text-${content?.alignment || 'center'} bg-gradient-to-r from-gray-100 to-gray-200`}>
                  {content?.imageUrl && (
                    <img 
                      src={content.imageUrl} 
                      alt="Hero" 
                      className="w-full h-32 object-cover rounded-lg mb-6"
                    />
                  )}
                  {content?.title && (
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">
                      {content.title}
                    </h1>
                  )}
                  {content?.subtitle && (
                    <h2 className="text-lg text-gray-600 mb-4">
                      {content.subtitle}
                    </h2>
                  )}
                  {content?.text && (
                    <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                      {content.text}
                    </p>
                  )}
                  {content?.buttonText && (
                    <div className={`inline-block ${
                      buttonStyles.find(s => s.value === content?.buttonStyle)?.class || buttonStyles[0].class
                    } text-sm`}>
                      {content.buttonText}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                This is how your hero section will look
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroForm;